import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link as JoyLink,
  List,
  ListItem,
  ListItemContent,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

import Page from "../../../components/Pages/Page";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/Loading";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useBKGRead, useBKGVCPersons } from "../hooks";
import { FloatingChatWidget } from "../components/BKGAgent";
import {
  useTutorialTarget,
  useTutorialHint,
} from "../../../components/Tutorial";
import {
  extractValue,
  ensureStringArray,
  formatLocation,
  formatCurrency,
  formatCheckSize,
} from "../utils/ventureCapital";
import AutodbTooltipWrapper, {
  AutodbTooltipHost,
  getLinkedinConfidenceLevel,
} from "../components/AutodbTooltipWrapper";
import LinkedInConfidenceBadge from "../components/LinkedInConfidenceBadge";
import { stripSourceRefs } from "../../../utils/stripSourceRefs";
import {
  buildCompanyHref,
  COMPANY_PROFILE_BACK_SOURCE,
  resolveRouteDocumentId,
  VC_PROFILE_BACK_SOURCE,
  useProfileBackTag,
  type VCProfileBackSource,
} from "../utils/profileBackNavigation";

type Fund = {
  name?: string;
  vintageYear?: string;
  sizeDisplay?: string;
  status?: string;
  stage?: string;
  type?: string;
  focus?: string[];
  notes?: string;
};

type Deal = {
  companyName?: string;
  companyId?: string;
  companyReference?: string;
  amountDisplay?: string;
  vcAmountDisplay?: string;
  stage?: string;
  date?: string;
  notes?: string;
  role?: string;
  leadInvestor?: boolean;
  coInvestors?: string[];
};

type Exit = {
  companyName?: string;
  exitType?: string;
  exitValue?: string;
  date?: string;
  notes?: string;
};

type PortfolioCompany = {
  id: string;
  name: string;
  reference: string;
};

type NormalizedVentureFirm = {
  name?: string;
  description?: string;
  location?: string;
  foundedYear?: string;
  checkSizeRange?: string;
  averageCheckSize?: string;
  aum?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  contactEmail?: string;
  phone?: string;
  focusSectors: string[];
  stageFocus: string[];
  investmentStyles: string[];
  internalCapabilities: string[];
  geographicFocus: string[];
  portfolioCompanies: string[];
  portfolioCompaniesData: PortfolioCompany[];
  notableInvestments: string[];
  investmentThesis?: string;
  notes?: string;
  keyPeople: any[]; // vc_person entities from lead_partners or manually added
  activeFunds: Fund[];
  recentDeals: Deal[];
  notableExits: Exit[];
  latestBiotechDeal?: Deal;
  latestNews?: string;
};

const nonEmptyString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const raw = typeof value === "string" ? value : String(value);
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "N/A" || trimmed === "—") return undefined;
  return trimmed;
};

const formatDateOnly = (dateString: string | undefined): string | undefined => {
  if (!dateString) return undefined;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch {
    return dateString; // Return original if parsing fails
  }
};

const isUnavailable = (value: string | undefined): boolean =>
  value === "Not available" || value === "N/A";

const currencyOrUndefined = (value: any): string | undefined => {
  const formatted = formatCurrency(value);
  return isUnavailable(formatted) ? undefined : formatted;
};

const checkSizeOrUndefined = (value: any): string | undefined => {
  const formatted = formatCheckSize(value);
  return isUnavailable(formatted) ? undefined : formatted;
};

const locationOrUndefined = (value: any): string | undefined => {
  if (!value) return undefined;
  const formatted = formatLocation(value);
  return isUnavailable(formatted) ? undefined : formatted;
};

const uniqueStrings = (values: string[]): string[] => {
  const set = new Set<string>();
  values.forEach((item) => {
    if (!item) return;
    const trimmed = item.trim();
    if (trimmed) set.add(trimmed);
  });
  return Array.from(set);
};

const extractPrimitiveValue = (input: any): string | undefined => {
  if (input === null || input === undefined) return undefined;
  if (typeof input === "string") return input;
  if (typeof input === "number" || typeof input === "boolean") {
    return String(input);
  }
  if (typeof input === "object") {
    if ("value" in input) {
      return extractPrimitiveValue((input as any).value);
    }
    if ("name" in input) {
      return extractPrimitiveValue((input as any).name);
    }
    if ("label" in input) {
      return extractPrimitiveValue((input as any).label);
    }
  }
  return undefined;
};

// Hidden until reworked (decided 2026-09-29). The indexer fills
// human_connections from co-mentions on public pages: a third are same-firm
// colleagues, a fifth are organisations or roles, and each list shares one
// source (28/40 sampled items confirmable). Re-enable once the field has a
// defined relationship type and one source per item.
const SHOW_HUMAN_CONNECTIONS = false;

const extractConnectionLabels = (person: any): string[] => {
  const rawConnections = person?.human_connections ?? person?.connections;
  const connectionArray = Array.isArray(rawConnections?.value)
    ? rawConnections.value
    : Array.isArray(rawConnections)
      ? rawConnections
      : rawConnections
        ? [rawConnections]
        : [];

  const labels = connectionArray
    .map((connection: any) => extractPrimitiveValue(connection))
    .filter((label: string | undefined): label is string => Boolean(label));

  return uniqueStrings(labels);
};

const resolveBoolean = (input: any): boolean => {
  if (input === null || input === undefined) return false;
  if (typeof input === "boolean") return input;
  if (typeof input === "string") {
    const normalized = input.trim().toLowerCase();
    if (!normalized) return false;
    return normalized === "true" || normalized === "yes" || normalized === "1";
  }
  if (typeof input === "number") {
    return input === 1;
  }
  if (typeof input === "object" && "value" in input) {
    return resolveBoolean((input as any).value);
  }
  return false;
};

const parseFunds = (fundEntries: any): Fund[] => {
  if (!Array.isArray(fundEntries)) return [];
  return fundEntries
    .map((entry) => {
      const node = entry?.value ?? entry;
      if (!node || typeof node !== "object") return null;

      const name = nonEmptyString(
        (node as any).name?.value ?? (node as any).name,
      );
      const vintageYear = nonEmptyString(
        (node as any).vintage_year?.value ?? (node as any).vintage_year,
      );
      const sizeDisplay = currencyOrUndefined(
        (node as any).size_usd?.value ?? (node as any).size_usd,
      );
      const status = nonEmptyString(
        (node as any).status?.value ?? (node as any).status,
      );
      const stage = nonEmptyString(
        (node as any).stage?.value ?? (node as any).stage,
      );
      const type =
        nonEmptyString((node as any).type?.value ?? (node as any).type) ??
        nonEmptyString(
          (node as any).fund_type?.value ?? (node as any).fund_type,
        );

      const focusRaw = (node as any).focus ?? (node as any).sector_focus;
      const focus = uniqueStrings(
        Array.isArray(focusRaw)
          ? focusRaw.map((f: any) => extractValue(f) || "")
          : [],
      );
      const notesRaw = nonEmptyString(
        (node as any).notes?.value ?? (node as any).notes,
      );
      const notes = notesRaw ? stripSourceRefs(notesRaw) : undefined;

      if (
        !name &&
        !vintageYear &&
        !sizeDisplay &&
        !status &&
        !stage &&
        !type &&
        !focus.length &&
        !notes
      ) {
        return null;
      }

      return {
        name,
        vintageYear,
        sizeDisplay,
        status,
        stage,
        type,
        focus,
        notes,
      } as Fund;
    })
    .filter((fund) => fund !== null) as Fund[];
};

const parseDeals = (dealEntries: any): Deal[] => {
  if (!Array.isArray(dealEntries)) return [];
  return dealEntries
    .map((entry) => {
      const node = entry?.value ?? entry;
      if (!node || typeof node !== "object") return null;

      // Handle company reference - it might be a Reference object
      const companyRef =
        (node as any).company?.value ??
        (node as any).company ??
        (node as any).portfolio_company?.value ??
        (node as any).portfolio_company ??
        (node as any).investment?.company;

      // If it's a reference, try to get the reference string to extract company name
      let companyName: string | undefined;
      let companyId: string | undefined;
      let companyReference: string | undefined;
      if (companyRef && typeof companyRef === "object") {
        if (companyRef.reference) {
          companyReference = companyRef.reference;
          // Extract company ID from reference (format: "workspace/company/company_id")
          const refParts = companyRef.reference.split("/");
          const companyIndex = refParts.findIndex(
            (part: string) => part === "company",
          );
          const extractedId =
            companyIndex >= 0
              ? refParts.slice(companyIndex + 1).join("/")
              : refParts[refParts.length - 1];
          companyId = extractedId;
          // Convert to readable name
          companyName = extractedId
            .replace(/_/g, " ")
            .split(" ")
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        } else if (companyRef.name) {
          companyName = nonEmptyString(extractValue(companyRef.name));
        }
      } else if (typeof companyRef === "string") {
        companyName = companyRef;
      }

      const amountDisplay = currencyOrUndefined(
        (node as any).amount_usd?.value ??
          (node as any).amount_usd ??
          (node as any).amount ??
          (node as any).size,
      );
      const vcAmountDisplay = currencyOrUndefined(
        (node as any).vc_amount_usd?.value ?? (node as any).vc_amount_usd,
      );
      const stage =
        nonEmptyString((node as any).round?.value ?? (node as any).round) ??
        nonEmptyString((node as any).stage?.value ?? (node as any).stage) ??
        nonEmptyString((node as any).series?.value ?? (node as any).series);
      const rawDate =
        nonEmptyString((node as any).date?.value ?? (node as any).date) ??
        nonEmptyString((node as any).year?.value ?? (node as any).year) ??
        nonEmptyString(
          (node as any).announcement_date?.value ??
            (node as any).announcement_date,
        );
      const date = formatDateOnly(rawDate);
      const notesRaw =
        nonEmptyString((node as any).notes?.value ?? (node as any).notes) ??
        nonEmptyString((node as any).details?.value ?? (node as any).details);
      const notes = notesRaw ? stripSourceRefs(notesRaw) : undefined;
      const role =
        nonEmptyString((node as any).role?.value ?? (node as any).role) ??
        nonEmptyString((node as any).lead?.value ?? (node as any).lead);
      const leadInvestor =
        ((node as any).lead_investor?.value ?? (node as any).lead_investor) ===
        true;

      // Handle co_investors array
      const coInvestorsRaw =
        (node as any).co_investors ??
        (node as any).coinvestors ??
        (node as any).partners;
      const coInvestors = uniqueStrings(
        Array.isArray(coInvestorsRaw)
          ? coInvestorsRaw.map((inv: any) => {
              if (inv && typeof inv === "object") {
                if (inv.reference) {
                  const refParts = inv.reference.split("/");
                  const vcId = refParts[refParts.length - 1];
                  return vcId
                    .split("_")
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() + word.slice(1),
                    )
                    .join(" ");
                }
                return extractValue(inv) || "";
              }
              return String(inv || "");
            })
          : [],
      );

      if (
        !companyName &&
        !amountDisplay &&
        !vcAmountDisplay &&
        !stage &&
        !date &&
        !notes &&
        !role &&
        !coInvestors.length &&
        !leadInvestor
      ) {
        return null;
      }

      return {
        companyName,
        companyId,
        companyReference,
        amountDisplay,
        vcAmountDisplay,
        stage,
        date,
        notes,
        role,
        leadInvestor,
        coInvestors,
      } as Deal;
    })
    .filter((deal) => deal !== null) as Deal[];
};

const parseExits = (exitEntries: any): Exit[] => {
  if (!Array.isArray(exitEntries)) return [];
  return exitEntries
    .map((entry) => {
      const node = entry?.value ?? entry;
      if (!node || typeof node !== "object") return null;

      const companyRef =
        (node as any).company?.value ??
        (node as any).company ??
        (node as any).portfolio_company?.value ??
        (node as any).portfolio_company;

      let companyName: string | undefined;
      if (companyRef && typeof companyRef === "object") {
        if (companyRef.reference) {
          // Extract company ID from reference (format: "workspace/company/company_id")
          const refParts = companyRef.reference.split("/");
          const companyIndex = refParts.findIndex(
            (part: string) => part === "company",
          );
          const companyId =
            companyIndex >= 0
              ? refParts.slice(companyIndex + 1).join("/")
              : refParts[refParts.length - 1];
          // Convert to readable name
          companyName = companyId
            .replace(/_/g, " ")
            .split(" ")
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        } else if (companyRef.name) {
          companyName = nonEmptyString(extractValue(companyRef.name));
        }
      } else if (typeof companyRef === "string") {
        companyName = companyRef;
      }

      const companyNameFinal =
        nonEmptyString(
          (node as any).company_name?.value ?? (node as any).company_name,
        ) ?? companyName;

      const exitType =
        nonEmptyString(
          (node as any).exit_type?.value ?? (node as any).exit_type,
        ) ?? nonEmptyString((node as any).type?.value ?? (node as any).type);
      const exitValue = currencyOrUndefined(
        (node as any).exit_value_usd?.value ??
          (node as any).exit_value_usd ??
          (node as any).exit_value?.value ??
          (node as any).exit_value ??
          (node as any).value?.value ??
          (node as any).value ??
          (node as any).valuation?.value ??
          (node as any).valuation,
      );
      const rawDate =
        nonEmptyString((node as any).date?.value ?? (node as any).date) ??
        nonEmptyString(
          (node as any).exit_date?.value ?? (node as any).exit_date,
        ) ??
        nonEmptyString((node as any).year?.value ?? (node as any).year);
      const date = formatDateOnly(rawDate);
      const notesRaw =
        nonEmptyString((node as any).notes?.value ?? (node as any).notes) ??
        nonEmptyString((node as any).details?.value ?? (node as any).details);
      const notes = notesRaw ? stripSourceRefs(notesRaw) : undefined;

      if (!companyNameFinal && !exitType && !exitValue && !date && !notes) {
        return null;
      }

      return {
        companyName: companyNameFinal,
        exitType,
        exitValue,
        date,
        notes,
      } as Exit;
    })
    .filter((exit) => exit !== null) as Exit[];
};

const DetailItem: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography
      level="body-xs"
      color="neutral"
      sx={{ fontWeight: 600, textTransform: "uppercase" }}
    >
      {label}
    </Typography>
    {value ? (
      typeof value === "string" ? (
        <Typography level="body-sm">{value}</Typography>
      ) : (
        value
      )
    ) : (
      <Typography level="body-sm" color="neutral">
        —
      </Typography>
    )}
  </Box>
);

const ChipList: React.FC<{ items: string[] }> = ({ items }) => {
  if (!items.length) {
    return (
      <Typography level="body-sm" color="neutral">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {items.map((item) => (
        <Chip
          key={item}
          size="md"
          variant="outlined"
          color="primary"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 1.5,
            py: 0.5,
          }}
        >
          {item}
        </Chip>
      ))}
    </Stack>
  );
};

const VentureCapitalProfile: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const { venture_capital_id } = useParams<{ venture_capital_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as {
    documentId?: string;
  } | null;
  const documentIdFromState = navigationState?.documentId;

  const BKGReadAPI = useBKGRead();
  const fetchVCPersons = useBKGVCPersons();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ventureFirm, setVentureFirm] = useState<any>(null);
  const [keyPeople, setKeyPeople] = useState<
    Array<{ _id: string; value: Record<string, any> }>
  >([]);

  // One-shot hint introducing the VC firm profile layout.
  const overviewTutorialRef = useTutorialTarget("bkg-vc-overview");
  useTutorialHint(
    {
      id: "bkg-vc-profile-intro",
      targetId: "bkg-vc-overview",
      title: "Inside a VC firm",
      body: "Review active funds, portfolio companies, recent deals and exits. Click any portfolio company to view its profile.",
      placement: "auto",
    },
    !loading && !!ventureFirm,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchProfile = async () => {
      if (!workspace?.id || !venture_capital_id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setKeyPeople([]);

      const resolvedDocumentId =
        documentIdFromState ??
        resolveRouteDocumentId(
          workspace.id,
          "venture_capital",
          venture_capital_id,
        );

      try {
        const [data, keyPeopleResponse] = await Promise.all([
          BKGReadAPI(workspace.id, "venture_capital", resolvedDocumentId),
          fetchVCPersons(workspace.id, resolvedDocumentId).catch(
            (keyPeopleError) => {
              console.error("Failed to fetch VC key people:", keyPeopleError);
              return { results: [] };
            },
          ),
        ]);
        if (!isCancelled) {
          setVentureFirm(data);
          setKeyPeople(keyPeopleResponse?.results ?? []);
        }
      } catch (err) {
        console.error("Failed to fetch venture capital profile:", err);
        if (!isCancelled) {
          setError("Failed to load venture capital profile. Please try again.");
          setVentureFirm(null);
          setKeyPeople([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      isCancelled = true;
    };
  }, [
    workspace?.id,
    venture_capital_id,
    documentIdFromState,
    BKGReadAPI,
    fetchVCPersons,
  ]);

  const normalized = useMemo<NormalizedVentureFirm | null>(() => {
    if (!ventureFirm?.value) return null;
    const value = ventureFirm.value as Record<string, any>;
    const fetchedKeyPeople = keyPeople.map((person) => ({
      ...(person?.value ?? {}),
      reference: person?._id ?? person?.value?.reference,
    }));

    const focusSectors = uniqueStrings(ensureStringArray(value.focus_sectors));
    const stageFocus = uniqueStrings(ensureStringArray(value.stage_focus));
    const investmentStyles = uniqueStrings(
      ensureStringArray(value.investment_style),
    );
    const internalCapabilities = uniqueStrings(
      ensureStringArray(value.internal_dev_capabilities),
    );

    // Parse portfolio_biotech references to extract company names and IDs
    const portfolioBiotechRaw =
      value.portfolio_biotech?.value ?? value.portfolio_biotech;
    const portfolioCompaniesData = Array.isArray(portfolioBiotechRaw)
      ? portfolioBiotechRaw
          .map((item: any) => {
            if (item && typeof item === "object" && item.reference) {
              // Extract company ID from reference (format: "workspace/company/company_id")
              const refParts = item.reference.split("/");
              // Find 'company' index and get everything after it
              const companyIndex = refParts.findIndex(
                (part: string) => part === "company",
              );
              const companyId =
                companyIndex >= 0
                  ? refParts.slice(companyIndex + 1).join("/")
                  : refParts[refParts.length - 1];
              // Convert underscores to spaces and title case, preserve forward slashes
              const companyName = companyId
                .replace(/_/g, " ")
                .split(" ")
                .map(
                  (word: string) =>
                    word.charAt(0).toUpperCase() + word.slice(1),
                )
                .join(" ");
              return {
                id: companyId,
                name: companyName,
                reference: item.reference,
              };
            }
            return null;
          })
          .filter(Boolean)
      : [];

    const portfolioCompanies = uniqueStrings(
      portfolioCompaniesData.map((c: any) => c.name),
    );

    const notableInvestments = uniqueStrings(
      ensureStringArray(value.notable_investments),
    );

    const descriptionRaw =
      nonEmptyString(extractValue(value.description)) ??
      nonEmptyString(extractValue(value.overview)) ??
      nonEmptyString(extractValue(value.summary)) ??
      nonEmptyString(extractValue(value.profile));
    const description = descriptionRaw
      ? stripSourceRefs(descriptionRaw)
      : undefined;

    const location =
      locationOrUndefined(value.headquarters?.value ?? value.headquarters) ??
      locationOrUndefined(value.location?.value ?? value.location);

    const checkSizeRange = checkSizeOrUndefined(
      value.check_size_usd?.value ?? value.check_size_usd,
    );
    const averageCheckSize = currencyOrUndefined(
      extractValue(value.average_check_size_usd),
    );
    const aum = currencyOrUndefined(extractValue(value.aum_usd));
    const foundedYear =
      nonEmptyString(extractValue(value.founded_year)) ??
      nonEmptyString(extractValue(value.established_year));

    const website =
      nonEmptyString(extractValue(value.webpage)) ??
      nonEmptyString(extractValue(value.website));
    const linkedin =
      nonEmptyString(extractValue(value.linkedin)) ??
      nonEmptyString(extractValue(value.linkedin_url));
    const twitter =
      nonEmptyString(extractValue(value.twitter)) ??
      nonEmptyString(extractValue(value.twitter_url));
    const contactEmail =
      nonEmptyString(extractValue(value.contact_email)) ??
      nonEmptyString(extractValue(value.email));
    const phone =
      nonEmptyString(extractValue(value.phone_number)) ??
      nonEmptyString(extractValue(value.phone));

    const investmentThesisRaw = nonEmptyString(
      extractValue(value.investment_thesis),
    );
    const investmentThesis = investmentThesisRaw
      ? stripSourceRefs(investmentThesisRaw)
      : undefined;
    const notesRaw = nonEmptyString(extractValue(value.notes));
    const notes = notesRaw ? stripSourceRefs(notesRaw) : undefined;

    // Use fetched vc_person entities (from lead_partners or manually added)
    // Fallback: merge with existing lead_partners and team_members arrays if vc_person index is not populated
    let keyPeopleData = fetchedKeyPeople.length > 0 ? fetchedKeyPeople : [];

    if (keyPeopleData.length === 0) {
      // Fallback to existing arrays on the firm document
      const leadPartnersArray =
        value.lead_partners?.value ?? value.lead_partners;
      const teamMembersArray = value.team_members?.value ?? value.team_members;

      const fallbackPeople = [];
      if (Array.isArray(leadPartnersArray)) {
        fallbackPeople.push(
          ...leadPartnersArray.map((p: any) => ({
            ...p,
            role: "Lead Partner",
          })),
        );
      }
      if (Array.isArray(teamMembersArray)) {
        fallbackPeople.push(...teamMembersArray);
      }
      keyPeopleData = fallbackPeople;
    }

    const activeFunds = parseFunds(
      value.active_funds?.value ?? value.active_funds,
    );
    const recentDeals = parseDeals(
      value.recent_deals?.value ?? value.recent_deals,
    );
    const notableExits = parseExits(
      value.notable_biotech_exits?.value ??
        value.notable_biotech_exits ??
        value.exits?.value ??
        value.exits,
    );

    // Parse latest_biotech_deal
    const latestDealRaw =
      value.latest_biotech_deal?.value ?? value.latest_biotech_deal;
    const latestBiotechDeal = latestDealRaw
      ? parseDeals([{ value: latestDealRaw }])[0]
      : undefined;

    // Latest News - extract from latest_news field (same as CompanyProfile)
    const latestNewsRaw = value.latest_news?.value ?? value.latest_news;
    const latestNewsStr =
      typeof latestNewsRaw === "string"
        ? latestNewsRaw
        : nonEmptyString(extractValue(latestNewsRaw));
    const latestNews = latestNewsStr
      ? stripSourceRefs(latestNewsStr)
      : undefined;

    return {
      name: nonEmptyString(extractValue(value.name)),
      description,
      location,
      foundedYear,
      checkSizeRange,
      averageCheckSize,
      aum,
      website,
      linkedin,
      twitter,
      contactEmail,
      phone,
      focusSectors,
      stageFocus,
      investmentStyles,
      internalCapabilities,
      geographicFocus: [], // Not in schema, always empty
      portfolioCompanies,
      portfolioCompaniesData: portfolioCompaniesData as PortfolioCompany[],
      notableInvestments,
      investmentThesis,
      notes,
      keyPeople: keyPeopleData, // vc_person entities (lead_partners + manually added)
      activeFunds,
      recentDeals,
      notableExits,
      latestBiotechDeal,
      latestNews,
    };
  }, [ventureFirm, keyPeople]);

  // See `useProfileBackTag` / `VC_PROFILE_BACK_SOURCE` for the contract.
  const backTag = useProfileBackTag<VCProfileBackSource>();
  // Require fromName for cross-profile, so label and destination always agree.
  const backsToCompanyProfile =
    backTag.from === VC_PROFILE_BACK_SOURCE.companyProfile &&
    !!backTag.fromId &&
    !!backTag.fromName;

  const backLabel = backsToCompanyProfile
    ? `Back to ${backTag.fromName}`
    : "Back to Results";

  // Tag every VC → Company link emitted from this view so the destination's
  // back button can return to *this* VC firm with an honest "Back to {name}"
  // label, both in-app and in a new tab. Accepts `undefined` because some call
  // sites have only narrowed at the JSX-guard level; returns "#" in that case.
  const companyHrefFromThisVC = (companyId: string | undefined): string => {
    if (!companyId || !workspace?.id) return "#";
    return buildCompanyHref(workspace.id, companyId, {
      from: COMPANY_PROFILE_BACK_SOURCE.vcProfile,
      fromId: venture_capital_id,
      fromName: normalized?.name ?? undefined,
    });
  };

  const handleBackClick = () => {
    if (!workspace?.id) return;
    // In-app history exists: pop it. The previous URL carries its own ?from=
    // tag (or no tag if it's a listing), so the label stays honest at each
    // step as the user backs up through the chain.
    if (!backTag.inFreshTabContext) {
      navigate(-1);
      return;
    }
    // Fresh tab / deep link / continuing a fresh-tab back chain: rebuild
    // from the tag and use replace + freshTabBack so the destination's back
    // button can keep using the fallback path without looping.
    if (backsToCompanyProfile) {
      // fromId is the decoded route param, which is a full document id
      // (with "/") for companies whose slug contains a slash.
      navigate(
        `/workspace/${workspace.id}/bkg/companies/${encodeURIComponent(backTag.fromId ?? "")}`,
        {
          replace: true,
          state: { freshTabBack: true },
        },
      );
    } else {
      navigate(`/workspace/${workspace.id}/bkg/venture-capital`, {
        replace: true,
      });
    }
  };

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      {/* One tooltip serves every wrapped cell on this page. */}
      <AutodbTooltipHost />

      <PageHeader>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            {backLabel}
          </Button>
          <Typography level="h1">
            {normalized?.name || "Venture Capital Firm"}
          </Typography>
        </Stack>
        {normalized?.website && (
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<LanguageIcon />}
            component="a"
            href={normalized.website}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit Website
          </Button>
        )}
      </PageHeader>

      <Stack spacing={3}>
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading />
        ) : !normalized ? (
          <Sheet variant="soft" sx={{ p: 4, textAlign: "center" }}>
            <Typography level="body-md">
              No venture capital data available.
            </Typography>
          </Sheet>
        ) : (
          <Stack spacing={3}>
            {/* VC Firm Header */}
            <Card variant="outlined" ref={overviewTutorialRef}>
              <CardContent>
                <Typography level="h2" sx={{ mb: 2, fontSize: "2rem" }}>
                  {normalized.name || "Venture Capital Firm"}
                </Typography>
                {normalized.description && (
                  <AutodbTooltipWrapper
                    block
                    info={
                      ventureFirm?.value?.summary ??
                      ventureFirm?.value?.description ??
                      ventureFirm?.value?.overview
                    }
                  >
                    <Typography level="body-lg" sx={{ mb: 3 }}>
                      {normalized.description}
                    </Typography>
                  </AutodbTooltipWrapper>
                )}

                {/* External Links */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <AutodbTooltipWrapper
                    info={
                      ventureFirm?.value?.webpage ?? ventureFirm?.value?.website
                    }
                  >
                    {normalized.website && (
                      <Button
                        component="a"
                        href={normalized.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="sm"
                        startDecorator={<LanguageIcon />}
                      >
                        Website
                      </Button>
                    )}
                  </AutodbTooltipWrapper>
                  <AutodbTooltipWrapper info={ventureFirm?.value?.linkedin}>
                    {normalized.linkedin && (
                      <Button
                        component="a"
                        href={normalized.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="sm"
                        startDecorator={<LinkedInIcon />}
                      >
                        LinkedIn
                      </Button>
                    )}
                  </AutodbTooltipWrapper>
                  {normalized.twitter && (
                    <Button
                      component="a"
                      href={normalized.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="sm"
                      startDecorator={<TwitterIcon />}
                    >
                      Twitter
                    </Button>
                  )}
                </Stack>

                <Grid container spacing={2}>
                  {normalized.location && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        block
                        info={ventureFirm?.value?.headquarters}
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 500 }}
                          >
                            Headquarters
                          </Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 400 }}>
                            {normalized.location}
                          </Typography>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {normalized.foundedYear && (
                    <Grid xs={12} md={3}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          Founded
                        </Typography>
                        <Typography level="body-sm">
                          {normalized.foundedYear}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {normalized.checkSizeRange && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        block
                        info={ventureFirm?.value?.check_size_usd}
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 600, textTransform: "uppercase" }}
                          >
                            Check Size
                          </Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 400 }}>
                            {normalized.checkSizeRange}
                          </Typography>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {normalized.aum && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        block
                        info={ventureFirm?.value?.aum_usd}
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 600, textTransform: "uppercase" }}
                          >
                            Assets Under Management
                          </Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 400 }}>
                            {normalized.aum}
                          </Typography>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {normalized.contactEmail && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        block
                        info={
                          ventureFirm?.value?.contact_email ??
                          ventureFirm?.value?.email
                        }
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 600, textTransform: "uppercase" }}
                          >
                            Contact Email
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <EmailIcon fontSize="small" />
                            <JoyLink
                              href={`mailto:${normalized.contactEmail}`}
                              level="body-sm"
                            >
                              {normalized.contactEmail}
                            </JoyLink>
                          </Stack>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {normalized.phone && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        block
                        info={
                          ventureFirm?.value?.phone_number ??
                          ventureFirm?.value?.phone
                        }
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 600, textTransform: "uppercase" }}
                          >
                            Phone
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <PhoneIcon fontSize="small" />
                            <Typography
                              level="body-sm"
                              sx={{ fontWeight: 400 }}
                            >
                              {normalized.phone}
                            </Typography>
                          </Stack>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {normalized.latestBiotechDeal && (
              <Card variant="outlined">
                <CardContent>
                  <AutodbTooltipWrapper
                    block
                    info={ventureFirm?.value?.latest_biotech_deal}
                  >
                    <Stack spacing={2}>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Latest Biotech Deal
                      </Typography>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {normalized.latestBiotechDeal.companyId &&
                          normalized.latestBiotechDeal.companyReference &&
                          workspace?.id ? (
                            <JoyLink
                              href={companyHrefFromThisVC(
                                normalized.latestBiotechDeal.companyId,
                              )}
                              component="a"
                              onClick={(e) => {
                                if (
                                  e.ctrlKey ||
                                  e.metaKey ||
                                  e.shiftKey ||
                                  e.button === 1
                                )
                                  return;
                                e.preventDefault();
                                if (normalized.latestBiotechDeal) {
                                  navigate(
                                    companyHrefFromThisVC(
                                      normalized.latestBiotechDeal.companyId,
                                    ),
                                    {
                                      state: {
                                        documentId:
                                          normalized.latestBiotechDeal
                                            .companyReference,
                                      },
                                    },
                                  );
                                }
                              }}
                              level="title-lg"
                              sx={{ fontWeight: 600, textAlign: "left" }}
                            >
                              {normalized.latestBiotechDeal.companyName ||
                                "Company Name Not Available"}
                            </JoyLink>
                          ) : (
                            <Typography
                              level="title-lg"
                              sx={{ fontWeight: 600 }}
                            >
                              {normalized.latestBiotechDeal.companyName ||
                                "Company Name Not Available"}
                            </Typography>
                          )}
                          {normalized.latestBiotechDeal.leadInvestor && (
                            <Chip size="sm" color="primary" variant="soft">
                              Lead Investor
                            </Chip>
                          )}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={2}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {normalized.latestBiotechDeal.stage && (
                            <Typography
                              level="body-sm"
                              sx={{ fontWeight: 400 }}
                            >
                              Round:{" "}
                              <strong>
                                {normalized.latestBiotechDeal.stage}
                              </strong>
                            </Typography>
                          )}
                          {normalized.latestBiotechDeal.amountDisplay && (
                            <Typography
                              level="body-sm"
                              sx={{ fontWeight: 400 }}
                            >
                              Amount:{" "}
                              <strong>
                                {normalized.latestBiotechDeal.amountDisplay}
                              </strong>
                            </Typography>
                          )}
                          {normalized.latestBiotechDeal.date && (
                            <Typography
                              level="body-sm"
                              sx={{ fontWeight: 400 }}
                            >
                              Date:{" "}
                              <strong>
                                {normalized.latestBiotechDeal.date}
                              </strong>
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </AutodbTooltipWrapper>
                </CardContent>
              </Card>
            )}

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    Investment Focus
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid xs={12} md={6}>
                      <Stack spacing={2}>
                        <AutodbTooltipWrapper
                          block
                          info={ventureFirm?.value?.focus_sectors}
                        >
                          <DetailItem
                            label="Focus Sectors"
                            value={<ChipList items={normalized.focusSectors} />}
                          />
                        </AutodbTooltipWrapper>
                        <AutodbTooltipWrapper
                          block
                          info={ventureFirm?.value?.stage_focus}
                        >
                          <DetailItem
                            label="Stage Focus"
                            value={<ChipList items={normalized.stageFocus} />}
                          />
                        </AutodbTooltipWrapper>
                        <AutodbTooltipWrapper
                          block
                          info={ventureFirm?.value?.investment_style}
                        >
                          <DetailItem
                            label="Investment Style"
                            value={
                              <ChipList items={normalized.investmentStyles} />
                            }
                          />
                        </AutodbTooltipWrapper>
                      </Stack>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <Stack spacing={2}>
                        <AutodbTooltipWrapper
                          block
                          info={ventureFirm?.value?.internal_dev_capabilities}
                        >
                          <DetailItem
                            label="Internal Capabilities"
                            value={
                              <ChipList
                                items={normalized.internalCapabilities}
                              />
                            }
                          />
                        </AutodbTooltipWrapper>
                      </Stack>
                    </Grid>
                  </Grid>
                  <Divider />
                  <Stack spacing={2}>
                    {(normalized.notableInvestments.length > 0 ||
                      normalized.recentDeals.length > 0) && (
                      <Box>
                        <Typography level="h3" sx={{ mb: 2 }}>
                          Notable Investments
                        </Typography>
                        <Stack spacing={1}>
                          {normalized.recentDeals
                            .slice(0, 5)
                            .map((deal, idx) =>
                              deal.companyName &&
                              deal.companyId &&
                              deal.companyReference &&
                              workspace?.id ? (
                                <Box key={`deal-${idx}`}>
                                  <JoyLink
                                    href={companyHrefFromThisVC(deal.companyId)}
                                    component="a"
                                    onClick={(e) => {
                                      if (
                                        e.ctrlKey ||
                                        e.metaKey ||
                                        e.shiftKey ||
                                        e.button === 1
                                      )
                                        return;
                                      e.preventDefault();
                                      navigate(
                                        companyHrefFromThisVC(deal.companyId),
                                        {
                                          state: {
                                            documentId: deal.companyReference,
                                          },
                                        },
                                      );
                                    }}
                                    level="title-sm"
                                    sx={{ fontWeight: 600, textAlign: "left" }}
                                  >
                                    {deal.companyName}
                                  </JoyLink>
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ mt: 0.5 }}
                                  >
                                    {deal.stage && (
                                      <Typography
                                        level="body-sm"
                                        color="neutral"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        Round: {deal.stage}
                                      </Typography>
                                    )}
                                    {deal.amountDisplay && (
                                      <Typography
                                        level="body-sm"
                                        color="neutral"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        Amount: {deal.amountDisplay}
                                      </Typography>
                                    )}
                                    {deal.date && (
                                      <Typography
                                        level="body-sm"
                                        color="neutral"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        Date: {deal.date}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              ) : deal.companyName ? (
                                <Box key={`deal-${idx}`}>
                                  <Typography
                                    level="title-sm"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {deal.companyName}
                                  </Typography>
                                </Box>
                              ) : null,
                            )}
                          {normalized.notableInvestments.map((inv) => (
                            <Typography
                              key={inv}
                              level="title-sm"
                              sx={{ fontWeight: 600 }}
                            >
                              {inv}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {normalized.investmentThesis && (
                      <DetailItem
                        label="Investment Thesis"
                        value={normalized.investmentThesis}
                      />
                    )}
                    {normalized.notes && (
                      <DetailItem label="Notes" value={normalized.notes} />
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {normalized.portfolioCompaniesData.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <AutodbTooltipWrapper
                    block
                    info={ventureFirm?.value?.portfolio_biotech}
                  >
                    <Stack spacing={2}>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Portfolio Companies
                      </Typography>
                      <Typography
                        level="body-sm"
                        color="neutral"
                        sx={{ mb: 1 }}
                      >
                        {normalized.portfolioCompaniesData.length} biotech{" "}
                        {normalized.portfolioCompaniesData.length === 1
                          ? "company"
                          : "companies"}{" "}
                        in portfolio
                      </Typography>
                      <Stack spacing={1}>
                        {normalized.portfolioCompaniesData.map((company) => (
                          <JoyLink
                            key={company.id}
                            href={
                              workspace?.id
                                ? companyHrefFromThisVC(company.id)
                                : undefined
                            }
                            component="a"
                            onClick={(e) => {
                              if (
                                e.ctrlKey ||
                                e.metaKey ||
                                e.shiftKey ||
                                e.button === 1
                              )
                                return;
                              e.preventDefault();
                              if (workspace?.id) {
                                navigate(companyHrefFromThisVC(company.id), {
                                  state: { documentId: company.reference },
                                });
                              }
                            }}
                            level="title-sm"
                            sx={{ fontWeight: 600, textAlign: "left" }}
                          >
                            {company.name}
                          </JoyLink>
                        ))}
                      </Stack>
                    </Stack>
                  </AutodbTooltipWrapper>
                </CardContent>
              </Card>
            )}

            {normalized.activeFunds.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <AutodbTooltipWrapper
                    block
                    info={ventureFirm?.value?.active_funds}
                  >
                    <Stack spacing={2}>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Active Funds
                      </Typography>
                      <List size="sm">
                        {normalized.activeFunds.map((fund) => (
                          <ListItem
                            key={[fund.name, fund.vintageYear, fund.sizeDisplay]
                              .filter(Boolean)
                              .join("-")}
                          >
                            <ListItemContent>
                              <Stack spacing={1}>
                                <Typography
                                  level="title-sm"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {fund.name || "Unnamed Fund"}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  {fund.vintageYear && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Vintage: {fund.vintageYear}
                                    </Typography>
                                  )}
                                  {fund.sizeDisplay && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Size: {fund.sizeDisplay}
                                    </Typography>
                                  )}
                                  {fund.stage && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Stage: {fund.stage}
                                    </Typography>
                                  )}
                                  {fund.status && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Status: {fund.status}
                                    </Typography>
                                  )}
                                  {fund.type && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Type: {fund.type}
                                    </Typography>
                                  )}
                                </Stack>
                                {fund.focus && fund.focus.length > 0 && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    {fund.focus.map((focus) => (
                                      <Chip
                                        key={focus}
                                        size="sm"
                                        variant="outlined"
                                      >
                                        {focus}
                                      </Chip>
                                    ))}
                                  </Stack>
                                )}
                                {fund.notes && (
                                  <Typography level="body-sm" color="neutral">
                                    {fund.notes}
                                  </Typography>
                                )}
                              </Stack>
                            </ListItemContent>
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                  </AutodbTooltipWrapper>
                </CardContent>
              </Card>
            )}

            {/* Key People Section - Lead Partners & Team Members */}
            {normalized.keyPeople &&
              Array.isArray(normalized.keyPeople) &&
              normalized.keyPeople.length > 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <AutodbTooltipWrapper
                      block
                      info={ventureFirm?.value?.lead_partners}
                    >
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Key People
                      </Typography>
                      <Grid container spacing={3}>
                        {normalized.keyPeople.map(
                          (person: any, index: number) => {
                            const personNameValue = extractValue(person?.name);
                            if (!personNameValue) {
                              return null;
                            }

                            const connections = SHOW_HUMAN_CONNECTIONS
                              ? extractConnectionLabels(person)
                              : [];

                            // Extract primitive values to avoid rendering objects
                            const personName =
                              extractValue(person?.name) ??
                              extractValue(person);
                            const personRole = extractValue(person?.role);
                            const personEmail = extractValue(person?.email);
                            const personLinkedin =
                              extractValue(person?.linkedin) ??
                              extractValue(person?.linkedin_url);
                            const personBiotechFocus = resolveBoolean(
                              person?.biotech_focus,
                            );

                            return (
                              <Grid xs={12} md={6} key={index}>
                                <Card
                                  variant="outlined"
                                  sx={{ height: "100%" }}
                                >
                                  <CardContent>
                                    <Stack spacing={1}>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <Typography
                                          level="title-md"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          {personName}
                                        </Typography>
                                        {personBiotechFocus && (
                                          <Chip
                                            size="sm"
                                            color="success"
                                            variant="soft"
                                          >
                                            Biotech Focus
                                          </Chip>
                                        )}
                                      </Stack>
                                      {personRole && (
                                        <Typography
                                          level="body-sm"
                                          color="neutral"
                                        >
                                          {personRole}
                                        </Typography>
                                      )}
                                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        {personEmail && (
                                          <Button
                                            component="a"
                                            href={`mailto:${personEmail}`}
                                            variant="plain"
                                            size="sm"
                                            startDecorator={<EmailIcon />}
                                            sx={{
                                              justifyContent: "flex-start",
                                            }}
                                          >
                                            {personEmail}
                                          </Button>
                                        )}
                                        {personLinkedin && (
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                          >
                                            <Button
                                              component="a"
                                              href={personLinkedin}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              variant="plain"
                                              size="sm"
                                              startDecorator={<LinkedInIcon />}
                                              sx={{
                                                justifyContent: "flex-start",
                                              }}
                                            >
                                              LinkedIn Profile
                                            </Button>
                                            <LinkedInConfidenceBadge
                                              level={getLinkedinConfidenceLevel(
                                                person?.linkedin ??
                                                  person?.linkedin_url,
                                              )}
                                            />
                                          </Stack>
                                        )}
                                      </Stack>
                                      {connections.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                          <Typography
                                            level="body-xs"
                                            sx={{
                                              fontWeight: 600,
                                              textTransform: "uppercase",
                                            }}
                                          >
                                            Human Connections
                                          </Typography>
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                            sx={{ mt: 0.5 }}
                                          >
                                            {connections.map((connection) => (
                                              <Chip
                                                key={connection}
                                                size="sm"
                                                variant="outlined"
                                              >
                                                {connection}
                                              </Chip>
                                            ))}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          },
                        )}
                      </Grid>
                    </AutodbTooltipWrapper>
                  </CardContent>
                </Card>
              )}

            {normalized.recentDeals.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <AutodbTooltipWrapper
                    block
                    info={ventureFirm?.value?.recent_deals}
                  >
                    <Stack spacing={2}>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Recent Deals
                      </Typography>
                      <List size="sm">
                        {normalized.recentDeals.map((deal) => (
                          <ListItem
                            key={[
                              deal.companyName,
                              deal.date,
                              deal.amountDisplay,
                            ]
                              .filter(Boolean)
                              .join("-")}
                          >
                            <ListItemContent>
                              <Stack spacing={1}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {deal.companyId &&
                                  deal.companyReference &&
                                  workspace?.id ? (
                                    <JoyLink
                                      href={companyHrefFromThisVC(
                                        deal.companyId,
                                      )}
                                      component="a"
                                      onClick={(e) => {
                                        if (
                                          e.ctrlKey ||
                                          e.metaKey ||
                                          e.shiftKey ||
                                          e.button === 1
                                        )
                                          return;
                                        e.preventDefault();
                                        navigate(
                                          companyHrefFromThisVC(deal.companyId),
                                          {
                                            state: {
                                              documentId: deal.companyReference,
                                            },
                                          },
                                        );
                                      }}
                                      level="title-sm"
                                      sx={{
                                        fontWeight: 600,
                                        textAlign: "left",
                                      }}
                                    >
                                      {deal.companyName || "Unnamed Company"}
                                    </JoyLink>
                                  ) : (
                                    <Typography
                                      level="title-sm"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {deal.companyName || "Unnamed Company"}
                                    </Typography>
                                  )}
                                  {deal.leadInvestor && (
                                    <Chip
                                      size="sm"
                                      color="primary"
                                      variant="soft"
                                    >
                                      Lead Investor
                                    </Chip>
                                  )}
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  {deal.stage && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Round: {deal.stage}
                                    </Typography>
                                  )}
                                  {deal.amountDisplay && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Total Round: {deal.amountDisplay}
                                    </Typography>
                                  )}
                                  {deal.vcAmountDisplay && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      VC Investment: {deal.vcAmountDisplay}
                                    </Typography>
                                  )}
                                  {deal.date && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Date: {deal.date}
                                    </Typography>
                                  )}
                                  {deal.role && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Role: {deal.role}
                                    </Typography>
                                  )}
                                </Stack>
                                {deal.coInvestors &&
                                  deal.coInvestors.length > 0 && (
                                    <Box>
                                      <Typography
                                        level="body-sm"
                                        sx={{ mb: 0.5, fontWeight: 600 }}
                                      >
                                        Co-Investors:
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                      >
                                        {deal.coInvestors.map((investor) => (
                                          <Chip
                                            key={investor}
                                            size="sm"
                                            variant="outlined"
                                          >
                                            {investor}
                                          </Chip>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                {deal.notes && (
                                  <Typography level="body-sm" color="neutral">
                                    {deal.notes}
                                  </Typography>
                                )}
                              </Stack>
                            </ListItemContent>
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                  </AutodbTooltipWrapper>
                </CardContent>
              </Card>
            )}

            {normalized.notableExits.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <AutodbTooltipWrapper
                    block
                    info={ventureFirm?.value?.notable_biotech_exits}
                  >
                    <Stack spacing={2}>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Notable Biotech Exits
                      </Typography>
                      <List size="sm">
                        {normalized.notableExits.map((exit, idx) => (
                          <ListItem
                            key={[
                              exit.companyName,
                              exit.date,
                              exit.exitValue,
                              idx,
                            ]
                              .filter(Boolean)
                              .join("-")}
                          >
                            <ListItemContent>
                              <Stack spacing={1}>
                                <Typography
                                  level="title-sm"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {exit.companyName || "Unnamed Company"}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  {exit.exitType && (
                                    <Chip
                                      size="sm"
                                      color="success"
                                      variant="soft"
                                    >
                                      {exit.exitType}
                                    </Chip>
                                  )}
                                  {exit.exitValue && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Value: {exit.exitValue}
                                    </Typography>
                                  )}
                                  {exit.date && (
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      Date: {exit.date}
                                    </Typography>
                                  )}
                                </Stack>
                                {exit.notes && (
                                  <Typography
                                    level="body-sm"
                                    color="neutral"
                                    sx={{ fontWeight: 400 }}
                                  >
                                    {exit.notes}
                                  </Typography>
                                )}
                              </Stack>
                            </ListItemContent>
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                  </AutodbTooltipWrapper>
                </CardContent>
              </Card>
            )}

            {/* Latest News Section - matches CompanyProfile design */}
            {normalized.latestNews && (
              <Card>
                <CardContent>
                  <AutodbTooltipWrapper
                    block
                    info={ventureFirm?.value?.latest_news}
                  >
                    <Typography level="h3" sx={{ mb: 2 }}>
                      Latest News
                    </Typography>
                    <Stack spacing={1}>
                      {normalized.latestNews
                        .split("\n\n")
                        .map((paragraph, index) => (
                          <Typography level="body-sm" key={index}>
                            {paragraph}
                          </Typography>
                        ))}
                    </Stack>
                  </AutodbTooltipWrapper>
                </CardContent>
              </Card>
            )}
          </Stack>
        )}
      </Stack>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

export default VentureCapitalProfile;
